import { call, put, takeLatest } from 'redux-saga/effects'
import {
  setLoading,
  unsetLoading,
} from 'portal-modules/common/redux/modules/loading'
import { getDocuments } from '../../api'
import { transformDocument } from '../../utils/transform-document'
import { documentsLoadingKey } from '../../constants/display-content'

const PREFIX = 'tenantImprovements'
export const FETCH_DOCUMENTS_REQUEST = `${PREFIX}/FETCH_DOCUMENTS_REQUEST`
export const FETCH_DOCUMENTS_SUCCESS = `${PREFIX}/FETCH_DOCUMENTS_SUCCESS`
export const FETCH_DOCUMENTS_FAILURE = `${PREFIX}/FETCH_DOCUMENTS_FAILURE`

// All three action creators carry the projectId in their payload so the
// reducer can update the right entry in the byId map. Without this, two
// detail pages opened in sequence would clobber each other's data.
export const fetchDocumentsRequest = (projectId) => ({
  type: FETCH_DOCUMENTS_REQUEST,
  payload: { projectId },
})

export const fetchDocumentsSuccess = (projectId, documents) => ({
  type: FETCH_DOCUMENTS_SUCCESS,
  payload: { projectId, documents },
})

export const fetchDocumentsFailure = (projectId, error) => ({
  type: FETCH_DOCUMENTS_FAILURE,
  payload: { projectId, error },
})

// Stable empty-array reference so `useSelector` does not see a new array
// identity on every render for projects that have no documents (or are
// still loading), which would cause a render loop in consumers that
// depend on the documents array.
const EMPTY_DOCUMENTS = Object.freeze([])

const DEFAULT_ENTRY = {
  documents: EMPTY_DOCUMENTS,
  isLoading: false,
  error: null,
  hasFetched: false,
}

const initialState = { byId: {} }

const updateEntry = (state, projectId, patch) => ({
  byId: {
    ...state.byId,
    [projectId]: {
      ...DEFAULT_ENTRY,
      ...state.byId[projectId],
      ...patch,
    },
  },
})

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_DOCUMENTS_REQUEST: {
      const { projectId } = action.payload
      return updateEntry(state, projectId, { isLoading: true, error: null })
    }
    case FETCH_DOCUMENTS_SUCCESS: {
      const { projectId, documents } = action.payload
      return updateEntry(state, projectId, {
        documents,
        isLoading: false,
        error: null,
        hasFetched: true,
      })
    }
    case FETCH_DOCUMENTS_FAILURE: {
      const { projectId, error } = action.payload
      return updateEntry(state, projectId, {
        isLoading: false,
        error,
        hasFetched: true,
      })
    }
    default:
      return state
  }
}

export default reducer

// Tolerant payload extractor: accept either a bare array or a wrapped
// `{ data: [...] }` envelope, since backends commonly choose either shape.
const extractDocuments = (response) => {
  const body = response?.data
  if (Array.isArray(body)) return body
  if (Array.isArray(body?.data)) return body.data
  if (Array.isArray(body?.documents)) return body.documents
  return []
}

export function* fetchDocumentsSaga(action) {
  const projectId = action?.payload?.projectId
  // Refuse to fan out to the API without a projectId. Hitting
  // /documents/projects/undefined would 404, but more importantly it
  // would defeat the whole point of the per-project cache. The
  // dispatcher (ProjectDocuments) is the single caller and always has
  // an id, so this branch is purely defensive — and we return BEFORE
  // any put(setLoading) so we never register a
  // `tenant-improvements-documents-undefined` loading key that nothing
  // ever clears (which would leak into Page.Content's spinner state).
  if (!projectId) return
  yield put(
    setLoading({
      key: documentsLoadingKey(projectId),
      message: __('Loading documents…'),
    }),
  )
  try {
    const response = yield call(getDocuments, projectId)
    const raw = extractDocuments(response)
    const documents = raw
      .map((doc) => transformDocument(doc))
      .filter((doc) => doc !== null)
    yield put(fetchDocumentsSuccess(projectId, documents))
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('fetchDocumentsSaga failed', error)
    }
    yield put(
      fetchDocumentsFailure(projectId, __('Failed to load documents')),
    )
  } finally {
    yield put(unsetLoading({ key: documentsLoadingKey(projectId) }))
  }
}

function* watchFetchDocuments() {
  yield takeLatest(FETCH_DOCUMENTS_REQUEST, fetchDocumentsSaga)
}

export const sagas = [watchFetchDocuments]

// ---- Selectors -----------------------------------------------------------
//
// All selectors are project-scoped factories: `selectFoo(projectId)(state)`.
// The previous global selectors (selectDocuments / selectDocumentsLoading)
// have been removed because they masked the cache-key bug that motivated
// this refactor — there's no correct "global documents" answer to give.

const SLICE = 'tenantImprovementDocuments'

const entryFor = (state, projectId) =>
  state?.[SLICE]?.byId?.[projectId] ?? DEFAULT_ENTRY

export const selectDocumentsByProjectId = (projectId) => (state) =>
  entryFor(state, projectId).documents ?? EMPTY_DOCUMENTS

export const selectDocumentsLoadingByProjectId = (projectId) => (state) =>
  Boolean(entryFor(state, projectId).isLoading)

export const selectDocumentsErrorByProjectId = (projectId) => (state) =>
  entryFor(state, projectId).error ?? null

// True only when (a) we have a real projectId, (b) nothing is in flight
// for that project, and (c) we have not yet completed a fetch for that
// project. Without the projectId guard, a SUCCESS for project 1 followed
// by a navigation to project 2 would never re-fire a fetch.
export const selectShouldFetchDocumentsByProjectId =
  (projectId) => (state) => {
    if (!projectId) return false
    const entry = state?.[SLICE]?.byId?.[projectId]
    if (!entry) return true
    return !entry.isLoading && !entry.hasFetched
  }
